import { useState } from "react";
import { createPortal } from "react-dom";

interface InfoTooltipProps {
  text: string;
  /** Tooltip panel widens for longer explanations — most callers are fine with the default. */
  wide?: boolean;
}

/**
 * Small (i) help icon — hover or click to reveal an explanation.
 *
 * Rendered into a body-level portal at a fixed screen position (same pattern
 * as AdminHomeDealBannersPage.tsx's Tooltip) rather than a plain CSS-absolute
 * popup — this component is used inside table headers and scrollable client
 * lists across the Rec Redis Health tab, and an absolutely-positioned popup
 * gets silently clipped by any ancestor `overflow-x-auto`/`overflow-y-auto`
 * container (every table wrapper and the client-list panel on that page), so
 * it never became visible there even though the icon itself rendered fine.
 */
const PANEL_WIDTH = { narrow: 256, wide: 320 } as const; // px, matches w-64 / w-80
const EDGE_MARGIN = 8;

export default function InfoTooltip({ text, wide }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; flipDown: boolean } | null>(null);

  const show = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const panelWidth = wide ? PANEL_WIDTH.wide : PANEL_WIDTH.narrow;
    const halfWidth = panelWidth / 2;

    // Clamp horizontally so the panel can never extend past either viewport
    // edge — an icon near the right edge of a table column previously let
    // the panel bleed off-screen and get truncated (the cut-off text you saw
    // in the connections table). left is a raw px offset from the viewport,
    // not the icon's own translate-centered point, once clamped.
    const idealLeft = rect.left + rect.width / 2;
    const left = Math.min(
      Math.max(idealLeft, halfWidth + EDGE_MARGIN),
      window.innerWidth - halfWidth - EDGE_MARGIN,
    );

    // Flip below the icon instead of above when there isn't roughly enough
    // headroom in the viewport — an icon near the top of a scrolled panel
    // otherwise opened a panel that started above row 0, which is what
    // pushed layout around rather than just rendering off the top edge.
    const flipDown = rect.top < 140;
    const top = flipDown ? rect.bottom + EDGE_MARGIN : rect.top - EDGE_MARGIN;

    setPos({ top, left, flipDown });
    setOpen(true);
  };

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onMouseEnter={show}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          show(e);
        }}
        aria-label="More info"
        className="ml-1 inline-flex items-center justify-center w-3.5 h-3.5 shrink-0 rounded-full border border-gray-600 text-[9px] leading-none text-gray-500 hover:text-white hover:border-gray-400 transition-colors"
      >
        i
      </button>
      {open && pos &&
        createPortal(
          <span
            style={{
              top: pos.top,
              left: pos.left,
              maxWidth: `calc(100vw - ${EDGE_MARGIN * 2}px)`,
              transform: pos.flipDown ? "translate(-50%, 0)" : "translate(-50%, -100%)",
            }}
            className={`fixed z-[9999] ${wide ? "w-80" : "w-64"} rounded-lg border border-gray-700 bg-gray-950 p-3 text-left text-xs leading-relaxed text-gray-300 normal-case font-normal shadow-2xl pointer-events-none`}
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}
